import React, { useState } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Dialog, DialogContent, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import PageWrapper from '../components/PageWrapper';
import SectionDivider from '../components/SectionDivider';

const Admissions = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [forumOpen, setForumOpen] = useState(false);

    return (
        <PageWrapper configKey="admissions" pageTitle="Admissions and Tuition">
            {(config) => (
                <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
                    <Typography variant="h3" component="h2" gutterBottom>
                        Admissions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        We are thrilled that you are interested in learning more about MacGillis Global Academy. We look forward to connecting with your family!
                        <br />
                        <br />
                        Please reach out to us at <a href="mailto:admission@macgillisglobalacademy.net">admission@macgillisglobalacademy.net</a> to schedule a time to chat.
                        {config.admissionPolicyUrl && (
                            <>
                                <br />
                                <br />
                                You can find our admission policy <a href={config.admissionPolicyUrl} target="_blank" rel="noopener noreferrer">here</a>.
                            </>
                        )}
                    </Typography>

                    {config.parentForum && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h4" component="h3" gutterBottom>
                                {config.parentForum.title}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { sm: 'flex-start' },
                                    gap: { xs: 0, sm: 4 },
                                }}
                            >
                                <Box sx={{ flex: 1, mb: { xs: 2, sm: 0 } }}>
                                    <Typography variant="body1">
                                        {config.parentForum.description}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: { xs: 'none', sm: 'flex' },
                                            justifyContent: 'flex-end',
                                            mt: 12,
                                            color: 'secondary.dark',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                gap: 1,
                                                transform: 'rotate(-3deg)',
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontFamily: '"Caveat", cursive',
                                                    fontSize: '2rem',
                                                    fontWeight: 700,
                                                    lineHeight: 1.05,
                                                    textAlign: 'right',
                                                    maxWidth: 165,
                                                }}
                                            >
                                                Check out the schedule!
                                            </Typography>
                                            <Box
                                                component="svg"
                                                viewBox="0 0 120 70"
                                                fill="none"
                                                aria-hidden="true"
                                                sx={{ width: 88, height: 'auto', flexShrink: 0, mb: 0.5 }}
                                            >
                                                <path
                                                    d="M4 52 C 30 64, 82 56, 110 22"
                                                    stroke="currentColor"
                                                    strokeWidth="3.5"
                                                    strokeLinecap="round"
                                                />
                                                <path
                                                    d="M110 22 L 92 25 M110 22 L 102 41"
                                                    stroke="currentColor"
                                                    strokeWidth="3.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 320 } }}>
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}${config.parentForum.image}`}
                                        alt={config.parentForum.title}
                                        onClick={() => setForumOpen(true)}
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 28px rgba(0, 0, 0, 0.22)',
                                            },
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        onClick={() => setForumOpen(true)}
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        Click to enlarge the schedule.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Typography variant="h4" component="h3" gutterBottom sx={{ mt: 4 }}>
                        Tuition and Cost
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>Program</TableCell>
                                    <TableCell sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>2025-26 Tuition</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Lower School (4<sup>th</sup> to 8<sup>th</sup> grade)</TableCell>
                                    <TableCell>${config.tuition.lowerSchool.toLocaleString()}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Upper School (9<sup>th</sup> to 12<sup>th</sup> grade)</TableCell>
                                    <TableCell>${config.tuition.upperSchool.toLocaleString()}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        For tuition and cost for international students, please contact us at <a href="mailto:admission@macgillisglobalacademy.net">admission@macgillisglobalacademy.net</a>.
                    </Typography>

                    <Typography variant="h4" component="h3" gutterBottom>
                        Discounts
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        • {config.tuition.siblingDiscount}% Sibling Discount, per sibling.
                    </Typography>

                    <Typography variant="h4" component="h3" gutterBottom>
                        How to Apply for Financial Aid
                    </Typography>
                    <Typography variant="body1">
                        Financial Aid is available for domestic students. We are proud to partner with FACTS Grant & Aid to collect your financial information, analyze the information, and render a grant recommendation. To find out more details, please contact us at <a href="mailto:admission@macgillisglobalacademy.net">admission@macgillisglobalacademy.net</a>.
                    </Typography>

                    <SectionDivider sx={{ my: 6 }} />

                    <Typography variant="h4" component="h3" gutterBottom>
                        Payment Plans
                    </Typography>
                    <TableContainer component={Paper} sx={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText', width: { xs: '26%', md: '32%' } }}>Payment Plan Option / Fees</TableCell>
                                    <TableCell sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText', width: { xs: '60%', md: '50%' } }}>Payment Schedule</TableCell>
                                    <TableCell sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText', width: { xs: '14%', md: '18%' } }}>Charge</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {config.paymentPlans.map((plan, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{plan.name}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'pre-line' }}>{plan.schedule}</TableCell>
                                        <TableCell>{plan.charge}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {config.parentForum && (
                        <Dialog
                            open={forumOpen}
                            onClose={() => setForumOpen(false)}
                            maxWidth={false}
                            fullScreen={isMobile}
                            PaperProps={{
                                sx: {
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    borderRadius: isMobile ? 0 : 2,
                                    overflow: 'hidden',
                                },
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    onClick={() => setForumOpen(false)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                        zIndex: 1,
                                    }}
                                >
                                    <Close />
                                </IconButton>
                                <DialogContent sx={{ p: 1, overflow: 'hidden' }}>
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}${config.parentForum.image}`}
                                        alt={config.parentForum.title}
                                        sx={{
                                            maxWidth: '95vw',
                                            maxHeight: '90vh',
                                            width: 'auto',
                                            height: 'auto',
                                            display: 'block',
                                            borderRadius: 1,
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                        }}
                                    />
                                </DialogContent>
                            </Box>
                        </Dialog>
                    )}
                </Container>
            )}
        </PageWrapper>
    );
};

export default Admissions; 